'use client';

import { c as _c } from "react/compiler-runtime";
import { execute } from '@data-access/lib/execute';
import { getGraphqlQueryKey, useGraphQL } from '@data-access/lib/graphql-query';
import type { QueryClient } from '@tanstack/react-query';
import type { PropsWithChildren } from 'react';
import { createContext, use } from 'react';
import { CartCurrencyDocument } from 'src/lib/backend/documents/cart';
const CartCurrencyContext = createContext<string | null>(null);
type CartCurrencyProviderProps = PropsWithChildren<{
  cartCurrency: string | null;
}>;

/**
 * Product pages: once cached, the query wins over the prop, because back/forward navigation
 * restores a page with its old server value.
 */
export function CartCurrencyProvider(t0) {
  const $ = _c(8);
  const {
    cartCurrency,
    children
  } = t0;
  let t1;
  if ($[0] !== cartCurrency) {
    t1 = {
      initialData: {
        cartCurrency
      }
    };
    $[0] = cartCurrency;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: t2
  } = useGraphQL(CartCurrencyDocument, undefined, t1);
  let t3;
  if ($[2] !== cartCurrency || $[3] !== t2) {
    t3 = t2 === undefined ? {
      cartCurrency
    } : t2;
    $[2] = cartCurrency;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const data = t3;
  const t4 = data.cartCurrency ?? null;
  let t5;
  if ($[5] !== children || $[6] !== t4) {
    t5 = <CartCurrencyContext value={t4}>{children}</CartCurrencyContext>;
    $[5] = children;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}

/**
 * Re-reads the cart currency even when no page observes it, so back/forward shows the current
 * value. A running read started before the cart change, so it is cancelled first.
 */
export function refreshCartCurrency(queryClient: QueryClient) {
  const queryKey = getGraphqlQueryKey(CartCurrencyDocument);
  void queryClient.cancelQueries({
    queryKey
  }).then(() => {
    void queryClient.invalidateQueries({
      queryKey
    });
    void queryClient.prefetchQuery({
      queryKey,
      queryFn: () => execute(CartCurrencyDocument)
    });
  });
}

/** Cart page: the currency comes from the cart query, which is already kept current. */
export function CartCurrencyValueProvider(t0) {
  const $ = _c(3);
  const {
    cartCurrency,
    children
  } = t0;
  let t1;
  if ($[0] !== cartCurrency || $[1] !== children) {
    t1 = <CartCurrencyContext value={cartCurrency}>{children}</CartCurrencyContext>;
    $[0] = cartCurrency;
    $[1] = children;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
export function useCartCurrency() {
  return use(CartCurrencyContext);
}
export function useIsCartCurrencyMismatch(productCurrency) {
  const cartCurrency = useCartCurrency();
  if (cartCurrency === null || productCurrency === undefined) {
    return false;
  }
  return cartCurrency !== productCurrency;
}